<template>
  <div class="login-page-wrapper">
    <div class="login-container">

      <div class="main-content">
        <!-- 登录区域容器 -->
        <div class="login-wrapper">
          <!-- 登录表单 -->
          <div class="login-form-container">
            <el-card class="login-card" shadow="always">
              <!-- 关闭按钮 -->
              <el-icon class="close-btn" @click="$router.push('/home')">
                <Close />
              </el-icon>

              <!-- 左右布局容器 -->
              <div class="login-body">
                <!-- 右侧表单区域 -->
                <div class="login-form-wrapper">
                  <!-- 顶部登录提示 - 动态标题 -->
                  <div class="login-header">
                    <h3>{{ getLoginTitle() }}</h3>
                  </div>

                  <!-- 账号登录表单 -->
                  <el-form
                      v-if="loginMethod === 'account'"
                      ref="accountLoginFormRef"
                      :model="accountLoginForm"
                      @submit.prevent="handleAccountLogin"
                      class="login-form"
                  >
                    <el-form-item prop="account">
                      <el-input
                          v-model="accountLoginForm.account"
                          placeholder="请输入用户名或手机号"
                          size="large"
                          clearable
                          class="login-elegant-input"
                          autocomplete="off"
                          @input="clearAccountErrors"
                      >
                        <template #prefix>
                          <el-icon><User /></el-icon>
                        </template>
                      </el-input>
                      <!-- 账号错误提示 -->
                      <div v-if="accountError" class="error-message">
                        <el-icon><WarningFilled /></el-icon>
                        <span>{{ accountError }}</span>
                      </div>
                    </el-form-item>

                    <el-form-item prop="password">
                      <el-input
                          v-model="accountLoginForm.password"
                          type="password"
                          placeholder="请输入密码"
                          size="large"
                          show-password
                          class="login-elegant-input"
                          autocomplete="new-password"
                          @input="clearPasswordErrors"
                      >
                        <template #prefix>
                          <el-icon><Lock /></el-icon>
                        </template>
                      </el-input>
                      <!-- 密码错误提示 -->
                      <div v-if="passwordError" class="error-message">
                        <el-icon><WarningFilled /></el-icon>
                        <span>{{ passwordError }}</span>
                      </div>
                    </el-form-item>

                    <!-- 忘记密码链接 -->
                    <div class="login-footer-link">
                      <el-link type="info" class="forgot-password" @click="$router.push('/updatePassword')">忘记密码?</el-link>
                    </div>

                    <el-form-item>
                      <el-button
                          type="primary"
                          size="large"
                          native-type="submit"
                          class="login-btn"
                          :loading="loading"
                      >
                        登录
                      </el-button>
                    </el-form-item>

                  </el-form>

                  <!-- 手机登录表单 -->
                  <el-form
                      v-if="loginMethod === 'phone'"
                      ref="phoneLoginFormRef"
                      :model="phoneLoginForm"
                      @submit.prevent="handlePhoneLogin"
                      class="login-form"
                  >
                    <el-form-item prop="phone">
                      <el-input
                          v-model="phoneLoginForm.phone"
                          placeholder="请输入手机号"
                          size="large"
                          clearable
                          class="login-elegant-input"
                          autocomplete="off"
                          @input="clearPhoneErrors"
                      >
                        <template #prefix>
                          <el-icon><Iphone /></el-icon>
                        </template>
                      </el-input>
                      <!-- 手机号错误提示 -->
                      <div v-if="phoneError" class="error-message">
                        <el-icon><WarningFilled /></el-icon>
                        <span>{{ phoneError }}</span>
                      </div>
                    </el-form-item>

                    <!-- 短信验证码 -->
                    <el-form-item prop="captcha">
                      <el-input
                          v-model="phoneLoginForm.captcha"
                          placeholder="请输入验证码"
                          size="large"
                          class="login-elegant-input"
                          autocomplete="off"
                          @input="clearCaptchaErrors"
                      >
                        <template #prefix>
                          <el-icon><Message /></el-icon>
                        </template>
                        <template #suffix>
                          <el-button
                              link
                              class="captcha-btn"
                              :disabled="captchaCooldown > 0"
                              @click="sendCaptcha"
                          >
                            {{ captchaCooldown > 0 ? `${captchaCooldown}秒后重试` : '获取验证码' }}
                          </el-button>
                        </template>
                      </el-input>
                      <!-- 验证码成功提示 -->
                      <div v-if="captchaSuccess" class="success-message">
                        <el-icon><SuccessFilled /></el-icon>
                        <span>{{ captchaSuccess }}</span>
                      </div>
                      <!-- 验证码错误提示 -->
                      <div v-if="captchaError" class="error-message">
                        <el-icon><WarningFilled /></el-icon>
                        <span>{{ captchaError }}</span>
                      </div>
                    </el-form-item>

                    <!-- 忘记密码链接 -->
                    <div class="login-footer-link">
                      <el-link type="info" class="forgot-password" @click="$router.push('/updatePassword')">忘记密码?</el-link>
                    </div>

                    <el-form-item>
                      <el-button
                          type="primary"
                          size="large"
                          native-type="submit"
                          class="login-btn"
                          :loading="loading"
                      >
                        登录
                      </el-button>
                    </el-form-item>

                  </el-form>

                  <!-- 微信登录表单 -->
                  <div v-if="loginMethod === 'wechat'" class="wechat-login-container">
                    <!-- 正常二维码显示 -->
                    <div v-if="wechatQrCodeUrl && !isQrCodeExpired" class="wechat-qrcode">
                      <img :src="wechatQrCodeUrl" alt="微信登录二维码">
                      <p class="tip">请使用微信扫一扫登录</p>
                    </div>

                    <!-- 二维码过期提示 -->
                    <div v-else-if="isQrCodeExpired" class="qr-expired-prompt">
                      <div class="expired-mask">
                        <div class="expired-content">
                          <el-icon class="expired-icon"><WarningFilled /></el-icon>
                          <p class="expired-text">二维码已过期</p>
                          <el-button
                              type="primary"
                              @click="refreshQrCode"
                              class="refresh-btn"
                              :loading="isWechatLoginLoading"
                          >
                            重新获取二维码
                          </el-button>
                        </div>
                      </div>

                      <!-- 显示过期的二维码（灰色覆盖） -->
                      <div class="expired-qrcode">
                        <img :src="wechatQrCodeUrl" alt="已过期的微信登录二维码" class="expired-img">
                      </div>
                    </div>

                    <div v-else class="loading-qrcode">
                      正在生成二维码...
                    </div>
                  </div>

                  <!-- 注册链接 -->
                  <div v-if="loginMethod !== 'wechat'" class="register-link">
                    <span>还没有账号？</span>
                    <el-link type="primary" @click="$router.push('/register')" style="font-style: italic;">去注册</el-link>
                  </div>

                  <!-- 登录方式选择 -->
                  <div class="login-method-wrapper">
                    <div class="login-method-container">
                      <div class="login-method-tabs">
                        <!-- 手机登录图标 - 当不是手机登录时显示 -->
                        <el-button
                            v-if="loginMethod !== 'phone'"
                            :class="['method-button', 'icon-only']"
                            @click="loginMethod = 'phone'"
                            size="large"
                            text
                        >
                          <svg t="1761617897590" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5998" width="28" height="28">
                            <path d="M724.350707 63.353535H297.787475C253.310707 63.353535 217.212121 99.555556 217.212121 143.928889v736.245656c0 44.476768 36.20202 80.575354 80.575354 80.575354h426.563232c44.476768 0 80.575354-36.20202 80.575354-80.575354V143.928889c0-44.476768-36.098586-80.575354-80.575354-80.575354zM297.787475 104.727273h426.563232c21.617778 0 39.201616 17.583838 39.201616 39.201616V179.717172H258.585859v-35.788283C258.585859 122.311111 276.169697 104.727273 297.787475 104.727273zM258.585859 221.090909h505.069899v527.72202H258.585859V221.090909z m465.764848 698.285253H297.787475c-21.617778 0-39.201616-17.583838-39.201616-39.201617v-89.987878h505.069899v89.987878c-0.103434 21.617778-17.687273 39.201616-39.305051 39.201617z m0 0" fill="#efb336" p-id="5999"></path>
                            <path d="M435.975758 149.721212h150.083232c8.274747 0 14.99798-6.723232 14.99798-14.99798 0-8.274747-6.723232-14.99798-14.99798-14.997979H435.975758c-8.274747 0-14.99798 6.723232-14.99798 14.997979 0 8.274747 6.723232 14.99798 14.99798 14.99798z m0 0M457.490101 855.660606c0 19.135354 10.214141 36.822626 26.789495 46.403232 16.575354 9.567677 37.003636 9.567677 53.57899 0a53.575111 53.575111 0 0 0 26.789495-46.403232c0-29.595152-23.983838-53.57899-53.57899-53.57899-29.595152 0-53.57899 23.983838-53.57899 53.57899z m0 0" fill="#efb336" p-id="6000"></path>
                          </svg>
                        </el-button>

                        <!-- 微信登录图标 - 当不是微信登录时显示 -->
                        <el-button
                            v-if="loginMethod !== 'wechat'"
                            :class="['method-button', 'icon-only']"
                            @click="handleWechatLogin"
                            :disabled="isWechatLoginLoading"
                            size="large"
                            text
                        >
                          <svg t="1761617723278" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4874" width="28" height="28">
                            <path d="M1024 619.52c0-143.36-138.24-256-307.2-256s-307.2 112.64-307.2 256 138.24 256 307.2 256c30.72 0 61.44-5.12 92.16-10.24l97.28 51.2-25.6-76.8c87.04-51.2 143.36-128 143.36-220.16z m-414.72-40.96c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z m209.92 0c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z" fill="#4CBF00" p-id="4875"></path>
                            <path d="M358.4 609.28c0-158.72 153.6-286.72 348.16-286.72h15.36c-40.96-133.12-179.2-235.52-353.28-235.52-204.8 0-368.64 138.24-368.64 307.2 0 107.52 66.56 204.8 168.96 256l-30.72 92.16L256 686.08c35.84 10.24 71.68 15.36 112.64 15.36h10.24c-15.36-30.72-20.48-61.44-20.48-92.16z m138.24-414.72c35.84 0 66.56 30.72 66.56 66.56s-30.72 66.56-66.56 66.56C460.8 322.56 430.08 291.84 430.08 256S460.8 194.56 496.64 194.56zM245.76 322.56c-35.84 0-61.44-30.72-61.44-66.56s30.72-66.56 66.56-66.56 61.44 30.72 61.44 66.56-30.72 66.56-66.56 66.56z" fill="#4CBF00" p-id="4876"></path>
                          </svg>
                        </el-button>

                        <!-- 账号登录图标 - 当不是账号登录时显示 -->
                        <el-button
                            v-if="loginMethod !== 'account'"
                            :class="['method-button', 'icon-only']"
                            @click="loginMethod = 'account'"
                            size="large"
                            text
                        >
                          <svg t="1761617971743" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="7068" width="28" height="28">
                            <path d="M522.24 61.44c112.64 0 204.8 92.16 204.8 204.8s-92.16 204.8-204.8 204.8-204.8-92.16-204.8-204.8 92.16-204.8 204.8-204.8m0-51.2c-143.36 0-256 112.64-256 256s112.64 256 256 256 256-112.64 256-256-112.64-256-256-256z" fill="#7dc5eb" p-id="7069"></path>
                            <path d="M512 471.04v51.2c71.68 0 133.12 10.24 168.96 15.36 15.36 0 30.72 10.24 46.08 15.36 10.24 5.12 15.36 5.12 25.6 10.24 163.84 76.8 220.16 209.92 199.68 307.2-10.24 40.96-35.84 92.16-87.04 97.28H153.6c-46.08 0-71.68-46.08-81.92-87.04-20.48-97.28 30.72-235.52 204.8-317.44 71.68-25.6 148.48-35.84 235.52-35.84V471.04m0 0c-76.8 0-168.96 10.24-256 40.96-337.92 158.72-276.48 501.76-102.4 501.76h716.8c179.2-15.36 220.16-353.28-102.4-501.76-5.12 0-46.08-15.36-76.8-20.48-46.08-10.24-107.52-20.48-179.2-20.48z" fill="#7dc5eb" p-id="7070"></path>
                          </svg>
                        </el-button>
                      </div>
                    </div>
                  </div>

                  <!-- 用户协议和隐私政策 -->
                  <div v-if="loginMethod !== 'wechat'" class="agreement">
                    <div class="agreement-content">
                      <el-checkbox
                          v-model="agreeToTerms"
                          class="agree-checkbox"
                          @click.stop
                      >
                        我已阅读并同意
                      </el-checkbox>
                      <div class="agreement-links">
                        <el-link type="primary" @click="showTermsDialog">《用户协议》</el-link>
                        <span>和</span>
                        <el-link type="primary" @click="showPrivacyDialog">《隐私政策》</el-link>
                      </div>
                    </div>
                  </div>
                  <div v-if="loginMethod === 'wechat'" class="agreement">
                    登录即表示同意
                    <el-link type="primary" @click="showTermsDialog">《用户协议》</el-link>
                    和
                    <el-link type="primary" @click="showPrivacyDialog">《隐私政策》</el-link>
                  </div>
                </div>
              </div>
            </el-card>
          </div>
        </div>
      </div>

    </div>

    <!-- 底部版权信息 -->
    <el-footer class="login-footer" height="auto">
      <div class="footer-content">
        <div class="footer-main">
          <div class="footer-sections">
            <!-- 网站介绍 -->
            <div class="footer-section">
              <div class="footer-logo">

              </div>
              <p class="footer-desc">
                专业的印前设计工具，让设计更加高效精准。我们致力于为各行业提供最优质的印前解决方案。
              </p>
            </div>

            <!-- 联系方式 -->
            <div class="footer-section">
              <h4 class="section-title">联系方式</h4>
              <div class="contact-info">
                <div class="contact-item">
                  <el-icon><Message /></el-icon>
                  <span>3052594895@qq.com</span>
                </div>
                <div class="contact-item">
                  <el-icon><Phone /></el-icon>
                  <span>18925330037</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 底部版权信息 -->
          <div class="footer-bottom">
            <div class="copyright-section">
              <div class="copyright-text">
                © 2025 中山市易印网络科技有限公司.保留所有权利.
              </div>

            </div>
            <div class="certification-info">
              <div class="beian-container">
                <img src="../../assets/police.gif" alt="公安备案图标" class="beian-icon">
                <a href="http://www.beian.gov.cn" target="_blank" class="beian-link">
                  粤公网安备 44200002443702号
                </a>
                <span class="divider">|</span>
                <a href="https://beian.miit.gov.cn/" target="_blank" class="icp-link">
                  粤ICP备16014837号
                </a>
              </div>
              <div class="legal-links">
                <a href="/privacyPolicy" @click.prevent="showPrivacyDialog" class="legal-link">隐私政策</a>
                <span class="divider">|</span>
                <a href="/userAgreement" @click.prevent="showTermsDialog" class="legal-link">用户协议</a>
                <span class="divider">|</span>
                <a class="legal-link">法律声明</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-footer>
  </div>

  <!-- 服务条款弹窗 -->
  <el-dialog
      v-model="termsDialogVisible"
      :title="null"
      width="60%"
      top="8vh"
      class="policy-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
  >
    <template #header>

    </template>
    <div class="dialog-content" v-html="termsContent"></div>
    <template #footer>
    <span class="dialog-footer">
      <el-button type="primary" @click="termsDialogVisible = false">关闭</el-button>
    </span>
    </template>
  </el-dialog>

  <!-- 隐私政策弹窗 -->
  <el-dialog
      v-model="privacyDialogVisible"
      :title="null"
      width="60%"
      top="8vh"
      class="policy-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="false"

  >
    <template #header>

    </template>
    <div class="dialog-content" v-html="privacyContent"></div>
    <template #footer>
    <span class="dialog-footer">
      <el-button type="primary" @click="privacyDialogVisible = false">关闭</el-button>
    </span>
    </template>
  </el-dialog>

  <BindPhoneDialog
      v-model="bindPhoneDialogVisible"
      :session-id="currentSessionId"
      @bind-success="handleBindSuccess"
  />
</template>

<script setup>
import { ref, reactive, watch, computed, onUnmounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Picture, Close, Message, Iphone, Star, SuccessFilled, WarningFilled, ChatDotSquare, Avatar } from '@element-plus/icons-vue'
import request from '@/utils/request'
import { eventBus } from '@/utils/eventBus'
import BindPhoneDialog from '@/components/BindPhoneDialog.vue'

const router = useRouter()
const route = useRoute() // 获取当前路由信息

// 登录方式
const loginMethod = ref('account') // account/phone/WeChat

// 获取登录标题
const getLoginTitle = () => {
  switch (loginMethod.value) {
    case 'account':
      return '账号登录'
    case 'phone':
      return '手机登录'
    case 'wechat':
      return '微信登录'
    default:
      return '登录'
  }
}

// 弹窗控制变量
const termsDialogVisible = ref(false)
const privacyDialogVisible = ref(false)

// 添加绑定手机弹窗相关变量
const bindPhoneDialogVisible = ref(false)

// 用户协议内容
const termsContent = ref(`
  <h2>易比图工具用户协议</h2>
  <p><strong>版本日期：2025年8月26日</strong></p>
  <p>本协议是您（以下简称"用户"）与易印公司（以下简称"本公司"）就使用易比图工具（以下简称"本软件"）服务所订立的协议。请您在注册、下载、安装或使用本软件及相关服务前，仔细阅读并充分理解本协议。</p>

  <h3>1. 服务内容</h3>
  <ul>
    <li>本软件提供屏幕截图图片比对与差异显示功能，主要用于包装印刷等视觉校对场景。</li>
    <li>本软件的比对结果仅供参考，最终决定应由用户自行或由专业人员确认。</li>
  </ul>

  <h3>2. 使用规范</h3>
  <ul>
    <li>用户不得将本软件用于任何违法、侵权或未经授权的用途。</li>
    <li>用户在使用本软件过程中产生的所有行为及后果，由用户自行承担。</li>
    <li>用户应自行做好数据备份和安全防护。</li>
  </ul>

  <h3>3. 知识产权</h3>
  <ul>
    <li>本软件及其全部内容，包括但不限于代码、界面设计、图标（含SVG图标）、文字、图像等，均受著作权法及相关法律保护，归本公司所有。</li>
    <li>未经本公司书面许可，用户不得复制、修改、出售、反向工程或以其他方式使用本软件的全部或部分内容。</li>
  </ul>

  <h3>4. 免责声明与责任限制</h3>
  <ul>
    <li>因用户依赖比对结果造成的任何直接或间接损失，本公司不承担责任。</li>
    <li>因不可抗力（包括但不限于自然灾害、网络故障、系统故障）导致的服务中断或数据丢失，本公司不承担责任。</li>
  </ul>

  <h3>5. 服务变更与终止</h3>
  <ul>
    <li>本公司保留随时修改、暂停或终止全部或部分服务的权利，并将在官网公告或通过适当方式提前通知用户。</li>
    <li>用户有权随时停止使用本软件。</li>
  </ul>

  <h3>6. 法律适用与争议解决</h3>
  <ul>
    <li>本协议的签订、履行、解释及争议解决均适用中华人民共和国法律。</li>
    <li>双方因本协议产生的争议，应友好协商解决；协商不成的，应提交至本公司所在地人民法院诉讼解决。</li>
  </ul>
`)
// 隐私政策内容
const privacyContent = ref(`
  <h2>易比图工具隐私政策</h2>
  <p><strong>版本日期：2025年8月26日</strong></p>
  <p>本隐私政策旨在说明本公司在您使用本软件及相关服务过程中，如何收集、使用、存储和保护您的个人信息。</p>

  <h3>1. 信息收集</h3>
  <ul>
    <li><strong>注册信息</strong>：包括姓名、邮箱、联系电话等（如适用）。</li>
    <li><strong>使用数据</strong>：包括软件功能使用频率、比对任务类型、错误日志（不含比对图片内容本身）。</li>
    <li><strong>设备信息</strong>：包括操作系统类型、版本号、硬件型号等。</li>
  </ul>

  <h3>2. 信息使用</h3>
  <ul>
    <li>提供、维护和优化本软件的功能与性能。</li>
    <li>为用户提供技术支持与更新通知。</li>
    <li>在用户同意的情况下，用于发送产品资讯或营销活动信息。</li>
  </ul>

  <h3>3. 信息存储与保护</h3>
  <ul>
    <li>本公司采取符合行业标准的安全措施保护用户数据，防止未经授权的访问、披露、篡改或毁坏。</li>
    <li>除法律法规另有规定外，本公司仅在为实现本政策所述目的所必需的期限内保存用户信息。</li>
  </ul>

  <h3>4. 信息共享</h3>
  <ul>
    <li>本公司不会向第三方出售或出租用户信息。</li>
    <li>仅在以下情况下共享用户信息：
      <ul>
        <li>获得用户事先同意；</li>
        <li>法律法规或政府机关要求；</li>
        <li>为维护本公司合法权益所必需。</li>
      </ul>
    </li>
  </ul>

  <h3>5. 用户权利</h3>
  <ul>
    <li>用户可随时访问、更正或删除其个人信息（法律法规另有规定的除外）。</li>
    <li>用户可撤回同意，但可能会影响某些功能的使用。</li>
  </ul>

  <h3>6. 政策更新</h3>
  <ul>
    <li>本公司可能会不时更新本政策，并通过官网或其他适当方式通知用户。</li>
    <li>更新后的政策自发布之日起生效。</li>
  </ul>
`)

// 显示对话框的方法
const showTermsDialog = () => {
  termsDialogVisible.value = true
}

const showPrivacyDialog = () => {
  privacyDialogVisible.value = true
}

// 同意协议相关的变量
const agreeToTerms = ref(false)
const agreeError = ref(false)

// 账号登录表单数据
const accountLoginForm = reactive({
  account: '',
  password: ''
})

// 手机登录表单数据
const phoneLoginForm = reactive({
  phone: '',
  captcha: ''
})

const emailLoginFormRef = ref(null)
const phoneLoginFormRef = ref(null)
const loading = ref(false)
const captchaCooldown = ref(0)      // 手机验证码发送冷却时间
const emailCaptchaCooldown = ref(0) // 邮箱验证码发送冷却时间

// 账号登录错误提示状态
const accountError = ref('')
const passwordError = ref('')

// 手机登录错误提示状态
const phoneError = ref('')
const captchaError = ref('')
const captchaSuccess = ref('')

const wechatQrCodeUrl = ref(''); // 微信二维码URL
const currentSessionId = ref(''); // 当前会话ID
const isQrCodeExpired = ref(false); // 二维码过期状态
const isWechatLoginLoading = ref(false); // 微信登录加载状态

// 轮询相关变量
let pollingTimer = null;
const activePollingSessions = ref(new Set()); // 活跃的轮询会话

// 清除邮箱错误提示
const clearEmailErrors = () => {
  emailError.value = '';
}

// 清除密码错误提示
const clearPasswordErrors = () => {
  passwordError.value = '';
}

// 清除账号错误提示
const clearAccountErrors = () => {
  accountError.value = '';
}

// 清除手机号错误提示
const clearPhoneErrors = () => {
  phoneError.value = '';
}

// 清除手机验证码错误提示
const clearCaptchaErrors = () => {
  captchaError.value = '';
  captchaSuccess.value = '';
}

// 验证账号格式
const validateAccount = (account) => {
  if (!account) return '请输入账号'
  return ''
}

// 验证密码
const validatePassword = (password) => {
  if (!password) return '请输入密码'
  if (password.length < 6) return '密码长度至少为6位'
  return ''
}

// 验证手机号格式
const validatePhone = (phone) => {
  if (!phone) return '请输入手机号'
  if (!/^1[3-9]\d{9}$/.test(phone)) return '请输入正确的手机号'
  return ''
}

// 验证验证码
const validateCaptcha = (captcha) => {
  if (!captcha) return '请输入验证码'
  if (captcha.length !== 6) return '验证码为6位数字'
  return ''
}

// 清理轮询函数
const clearPolling = () => {
  // console.log('清理轮询定时器:', pollingTimer);
  if (pollingTimer) {
    clearTimeout(pollingTimer);
    pollingTimer = null;
  }
}

// 清理所有轮询会话
const clearAllPolling = () => {
  clearPolling();
  activePollingSessions.value.clear();
  // console.log('所有轮询已清理');
}

// 倒计时函数
const startCountdown = (countdownRef, seconds = 60) => {
  countdownRef.value = seconds
  const timer = setInterval(() => {
    countdownRef.value--
    if (countdownRef.value <= 0) clearInterval(timer)
  }, 1000)
}

// 处理账号登录
const handleAccountLogin = async () => {
  if (!agreeToTerms.value) {
    ElMessage.error('请先阅读并同意用户协议和隐私政策')
    return
  }

  // 清除之前的错误信息
  clearAccountErrors()
  clearPasswordErrors()

  // 表单验证
  let isValid = true

  accountError.value = validateAccount(accountLoginForm.account)
  if (accountError.value) isValid = false

  passwordError.value = validatePassword(accountLoginForm.password)
  if (passwordError.value) isValid = false

  if (!isValid) return

  try {
    loading.value = true

    const response = await request.post('/auth/login/account', {
      account: accountLoginForm.account,
      password: accountLoginForm.password,
      type: '1',
      sin: ''
    });

    if (response.code === '200') {
      const { token, hasBoundPhone } = response.data;

      // 确保token存储完成
      localStorage.setItem('auth_token', token);
      console.log('Token存储完成:', localStorage.getItem('auth_token'));

      // 触发登录事件，让Layout开始加载用户信息
      eventBus.emitLogin();

      // 等待用户信息加载完成
      await new Promise(resolve => setTimeout(resolve, 500));

      if (hasBoundPhone) {
        ElMessage.success('登录成功');
        const redirectPath = getRedirectPath();
        console.log('准备跳转到:', redirectPath);
        clearRedirectPath();
        router.push(redirectPath);
      } else {
        // 跳转绑定手机页面，同时传递重定向路径
        const redirectPath = getRedirectPath();
        router.push({
          path: '/bind-phone',
          query: {
            token: token,
            redirect: redirectPath // 传递重定向路径
          }
        });
      }
    } else {
      ElMessage.error(response.message || '登录失败');
    }
  } catch (error) {
    // 处理错误
    if (error.response?.data?.message) {
      if (error.response.data.message.includes('账号')) {
        accountError.value = error.response.data.message;
      } else if (error.response.data.message.includes('密码')) {
        passwordError.value = error.response.data.message;
      } else {
        ElMessage.error(error.response.data.message);
      }
    }
    console.error('登录错误:', error);
  } finally {
    loading.value = false
  }
}

// 处理手机登录
const handlePhoneLogin = async () => {

  if (!agreeToTerms.value) {
    ElMessage.error('请先阅读并同意用户协议和隐私政策')
    return
  }

  // 清除之前的错误信息
  clearPhoneErrors()
  clearCaptchaErrors()

  // 表单验证
  let isValid = true

  phoneError.value = validatePhone(phoneLoginForm.phone)
  if (phoneError.value) isValid = false

  captchaError.value = validateCaptcha(phoneLoginForm.captcha)
  if (captchaError.value) isValid = false

  if (!isValid) return

  try {
    loading.value = true

    const response = await request.post('/auth/login/phone', {
      phone: phoneLoginForm.phone,
      verifyCode: phoneLoginForm.captcha,
      type: '1',
      sin: ''
    })

    if (response.code === '200') {
      const { token, hasBoundPhone } = response.data;
      console.log("后端传回", token)

      // 确保token存储完成
      localStorage.setItem('auth_token', token)
      console.log('Token存储完成:', localStorage.getItem('auth_token'));

      // 触发登录事件
      eventBus.emitLogin();

      // 等待用户信息加载完成
      await new Promise(resolve => setTimeout(resolve, 500));

      ElMessage.success('登录成功')
      const redirectPath = getRedirectPath();
      clearRedirectPath();
      router.push(redirectPath);
    } else {
      ElMessage.error(response.message || '登录失败')
    }
  } catch (error) {

    if (error.response.data.message) {
      if (error.response.data.message.includes('校验码')) {
        captchaError.value = '校验码已过期，请重新获取'
      } else if (error.response.data.message.includes('验证码')) {
        captchaError.value = '验证码错误，请重新输入'
      } else if (error.response.data.message.includes('手机')) {
        phoneError.value = '该手机号未注册'
      }  else {
        ElMessage.error(error.message)
      }
    }

    console.error('登录错误:', error.response.data)
  } finally {
    loading.value = false
  }
}

// 发送手机验证码
const sendCaptcha = async () => {
  // 清除之前的错误信息
  clearPhoneErrors()
  clearCaptchaErrors()

  // 验证手机号
  phoneError.value = validatePhone(phoneLoginForm.phone)
  if (phoneError.value) return

  try {
    const response = await request.get('/auth/verify-code/phone', {
      params: {
        phone: phoneLoginForm.phone,
        scene: 'Login' // 指定登录场景
      }
    })

    if (response.code === '200') {
      startCountdown(captchaCooldown, 60)
      captchaSuccess.value = '短信验证码已发送，可能会有延后，请耐心等待'
    } else {
      ElMessage.error(response.message || '验证码发送失败')
      console.log(response)
    }
  } catch (error) {
    ElMessage.error('验证码发送错误: ' + (error.response?.data?.message || error.message))
  }
}

// 处理微信登录
const handleWechatLogin = async () => {
  if (isWechatLoginLoading.value) return;

  isWechatLoginLoading.value = true;

  try {
    // console.log('开始微信登录，当前轮询状态:', pollingTimer);

    // 先清理所有现有的轮询
    clearAllPolling();

    // 重置过期状态
    isQrCodeExpired.value = false;

    // 切换到微信登录方式
    loginMethod.value = 'wechat';

    // 重置状态
    wechatQrCodeUrl.value = '';

    // 获取二维码和sessionId
    const response = await request.get('/auth/wechat/mp-qrcode');
    const { qrUrl, sessionId } = response.data;

    wechatQrCodeUrl.value = qrUrl;
    currentSessionId.value = sessionId;

    // 记录当前活跃的session
    activePollingSessions.value.add(sessionId);

    // 启动轮询检查登录状态
    startPolling();
  } catch (error) {
    console.error('获取二维码失败:', error);
    // ElMessage.error('获取微信登录二维码失败');
  } finally {
    isWechatLoginLoading.value = false;
  }
};

// 重新生成二维码
const refreshQrCode = () => {
  handleWechatLogin();
};

// 启动轮询
const startPolling = () => {
  clearPolling(); // 先清理之前的轮询

  const poll = async () => {
    // 多重检查确保应该停止轮询
    if (loginMethod.value !== 'wechat' || !activePollingSessions.value.has(currentSessionId.value)) {
      // console.log('轮询条件不满足，停止轮询');
      clearPolling();
      return;
    }

    try {
      const statusRes = await request.get('/auth/wechat/login/status', {
        params: { sessionId: currentSessionId.value }
      });

      const responseData = statusRes.data || {};

      // console.log('轮询响应:', responseData.status);

      if (responseData.status === 'success') {
        const token = responseData.token;
        const hasBoundPhone = responseData.hasBoundPhone;

        try {
          localStorage.setItem('auth_token', token);
          console.log('Token 存储成功:', localStorage.getItem('auth_token'));

          // 触发登录事件
          eventBus.emitLogin();

          // 等待用户信息加载完成
          await new Promise(resolve => setTimeout(resolve, 500));

          if (hasBoundPhone) {
            ElMessage.success('微信登录成功');
            clearAllPolling();
            const redirectPath = getRedirectPath();
            clearRedirectPath();
            router.push(redirectPath);
          } else {
            bindPhoneDialogVisible.value = true;
          }
        } catch (error) {
          console.error('Token 存储失败:', error);
          ElMessage.error('登录状态保存失败，请重试');
          return;
        }
    } else if (responseData.status === 'waiting_bind') {
        // 显示绑定手机弹窗
        bindPhoneDialogVisible.value = true;
      } else if (responseData.status === 'expired' || responseData.status === null) {
        // ！！！重要：会话过期时停止轮询！！！
        console.log('会话过期，停止轮询');
        isQrCodeExpired.value = true;
        clearAllPolling(); // 立即停止轮询
      } else if (responseData.status === 'waiting') {
        // 继续轮询，但添加额外检查
        if (loginMethod.value === 'wechat' && activePollingSessions.value.has(currentSessionId.value)) {
          pollingTimer = setTimeout(poll, 2000);
        } else {
          clearPolling();
        }
      }
    } catch (error) {
      console.error('轮询错误:', error);
      // 发生错误时也考虑停止轮询
      if (loginMethod.value === 'wechat' && activePollingSessions.value.has(currentSessionId.value)) {
        pollingTimer = setTimeout(poll, 2000);
      } else {
        clearPolling();
      }
    }
  };

  pollingTimer = setTimeout(poll, 2000);
  // console.log('启动轮询，定时器ID:', pollingTimer);
}

// 绑定成功处理
const handleBindSuccess = () => {
  ElMessage.success('绑定成功！')
  clearAllPolling();

  // 触发登录事件
  eventBus.emitLogin();

  // 等待用户信息加载完成后再跳转
  setTimeout(() => {
    const redirectPath = getRedirectPath();
    clearRedirectPath();
    router.push(redirectPath);
  }, 500);
}

// 重定向路径的辅助函数
const getRedirectPath = () => {
  return sessionStorage.getItem('redirect_path') || route.query.redirect || '/home';
};

const clearRedirectPath = () => {
  sessionStorage.removeItem('redirect_path');
};

// 监听登录方式变化，重置同意状态和清理轮询
watch(loginMethod, (newVal, oldVal) => {
  if (oldVal === 'wechat' && newVal !== 'wechat') {
    clearAllPolling();
  }
  if (newVal !== oldVal) {
    agreeToTerms.value = false;
    agreeError.value = false;
  }
});

// 路由离开时清理
onBeforeRouteLeave((to, from) => {
  if (from.name === 'login') {
    // console.log('离开登录页，清理轮询');
    clearAllPolling();
  }
});

// 组件销毁时彻底清理
onBeforeUnmount(() => {
  // console.log('组件即将销毁，清理轮询');
  clearAllPolling();
});

onUnmounted(() => {
  // console.log('组件已销毁，确保轮询清理');
  clearAllPolling();
});

</script>

<style scoped lang="scss">
/* 错误信息样式 */
.error-message {
  display: flex;
  align-items: center;
  color: #f84949;
  font-size: 12px;
  margin-top: 4px;
  height: 20px; /* 固定高度 */
  opacity: 1;
  transition: opacity 0.3s ease;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

/* 成功信息样式调整 */
.success-message {
  display: flex;
  align-items: center;
  color: #67c23a;
  font-size: 12px;
  margin-top: 4px;
  height: 20px; /* 固定高度 */
  opacity: 1;
  transition: opacity 0.3s ease;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

.login-page-wrapper {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: fixed;   // 绝对定位
}

.login-container {
  width: 100%;
  height: 65vh;
  background: url('@/assets/login/lr-bg.gif') ;
  background-size: cover;
  position: relative;
  overflow: hidden;
}

.main-content {
  display: flex;
  flex-direction: column;
  justify-content: center; // 垂直居中
  align-items: center;
  width: 100%;
  margin-top: 0; // 移除顶部边距
}

.login-wrapper {
  display: flex;
  width: 380px;
  height: 500px;
  max-width: 90%; // 改为百分比，更灵活
  max-height: 90vh; // 限制最大高度
  background: white;
  overflow: hidden;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  transform: translateX(30vw);
  margin-top: 20px;
}

.login-form-container {
  width: 100%;
  position: relative;
  z-index: 2;
  max-width: 700px;
  padding: 0;

  .login-card {
    width: 100%;
    overflow: hidden;
    box-shadow: none;
    position: relative;
    padding: 0;
    background: white;
    border: none;

    :deep(.el-card__body) {
      padding: 0 !important;
    }
  }
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 18px;
  cursor: pointer;
  color: #999;
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    color: #409EFF;
    transform: scale(1.1);
  }
}

/* 左右布局 */
.login-body {
  height: 500px;
  margin-top: 0 !important;
  padding: 0 !important;
  display: flex;
  max-height: 100%; // 限制高度
}

/* 右侧表单区域 */
.login-form-wrapper {
  flex: 1;
  padding: 10px 40px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 500px;
}

/* 登录头部 */
.login-header {
  margin-bottom: 25px;
  text-align: center;

  h3 {
    font-size: 24px;
    color: #2c3e50;
    margin-bottom: 8px;
    font-weight: 600;
  }
}

/* 登录表单通用样式 */
.login-form {
  min-height: 220px;
  margin: 0 !important;
  flex-direction: column;
  justify-content: center;
}

.login-method-wrapper {
  margin-top: 10px;
  margin-bottom: 0;
}

.login-method-container {
  background-color: transparent;
  border-radius: 8px;
  padding: 0;
  box-shadow: none;
  border: none;
}

/* 登录方式图标样式 */
.login-method-tabs {
  .method-icon {
    transition: all 0.3s ease;
  }

  .phone-icon {
    color: #409EFF; /* 蓝色 */
  }

  .user-icon {
    color: #409EFF; /* 蓝色 */
  }

  .wechat-icon {
    color: #09BB07; /* 微信绿色 */
  }

  .method-button {
    &.active {
      .phone-icon,
      .user-icon {
        color: #409EFF !important; /* 激活状态保持蓝色 */
      }

      .wechat-icon {
        color: #09BB07 !important; /* 激活状态保持绿色 */
      }
    }

    &:hover:not(.active) {
      .phone-icon,
      .user-icon {
        color: #66b1ff !important; /* 悬停时浅蓝色 */
      }

      .wechat-icon {
        color: #67c23a !important; /* 悬停时浅绿色 */
      }
    }
  }
}

.login-method-tabs {
  display: flex;
  justify-content: center;
  gap: 15px;
  position: relative;
  border-bottom: none;
  margin-bottom: 0;

  .method-button {
    position: relative;
    padding: 8px !important;
    margin: 0 !important;
    border: none !important;
    background: transparent !important;
    height: auto !important;
    border-radius: 50% !important;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.icon-only {
      .method-text {
        display: none;
      }
    }

    &.active {
      background: #ecf5ff !important;

      .phone-icon,
      .user-icon {
        color: #409EFF !important;
      }

      .wechat-icon {
        color: #09BB07 !important;
      }
    }

    &:hover {
      background: #f5f7fa !important;
      transform: translateY(-2px);
    }

    .method-icon {
      width: 28px;
      height: 28px;
    }

    .method-text {
      position: relative;
      z-index: 1;
      font-size: 14px;
      font-weight: 500;
    }
  }
}

/* 表单项目间距优化 */
:deep(.el-form-item) {
  margin-bottom: 20px;
}

/* 忘记密码链接 */
.login-footer-link {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .el-link {
    font-size: 13px;
    transition: all 0.3s;

    &:hover {
      color: #409EFF;
    }
  }
}

@keyframes underline-grow {
  0% {
    width: 0;
  }
  100% {
    width: 60%;
  }
}

.login-elegant-input {
  :deep(.el-input__wrapper) {
    border-radius: 0;
    padding: 1px 12px;
    box-shadow: 0 0 0 1px #dcdfe6 inset;
    height: 38px;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 0 0 1px #c0c4cc inset;
    }

    &.is-focus {
      box-shadow: 0 0 0 1px #409EFF inset;
    }
  }

  :deep(.el-input__prefix) {
    left: 8px;
    color: #606266;
  }

  .prefix-text {
    display: inline-block;
    width: 70px;
    text-align: left;
    padding-right: 8px;
    box-sizing: border-box;
    font-size: 14px;
    color: #373737;
  }

  :deep(.el-input__suffix) {
    display: flex;
    align-items: center;
    padding-right: 8px;
  }

  :deep(.el-input__inner) {
    padding-right: 90px; // 给按钮留出空间
  }

  .captcha-btn {
    padding: 0;
    height: auto;
    color: #373737;
    font-size: 13px;
    white-space: nowrap;
    margin-left: 4px;

    &:hover {
      color: #66b1ff;
    }

    &:disabled {
      color: #c0c4cc;
      cursor: not-allowed;
    }
  }

  :deep(input) {
    font-size: 14px;
    height: 16px;
    line-height: 36px;

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-text-fill-color: #606266 !important;
      -webkit-box-shadow: 0 0 0px 1000px white inset !important;
      transition: background-color 5000s ease-in-out 0s;
    }
  }
}

/* 微信登录容器优化 */
.wechat-login-container {
  height: 200px;
  margin: 0 !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  position: relative;

  .wechat-qrcode {
    text-align: center;
    margin-bottom: 20px;

    img {
      width: 200px;
      height: 200px;
      display: block;
      margin: 0 auto;
      border: 1px solid #f0f0f0;
    }

    .tip {
      margin-top: 15px;
      color: #606266;
      font-size: 14px;
      text-align: center;
    }
  }

  .loading-qrcode {
    padding: 60px 0;
    color: #909399;
    font-size: 14px;
    text-align: center;
  }
}

/* 二维码过期样式 */
.qr-expired-prompt {
  position: relative;
  width: 100%;
  height: 100%;
}

.expired-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}

.expired-content {
  text-align: center;
  padding: 20px;
}

.expired-icon {
  font-size: 48px;
  color: #e6a23c;
  margin-bottom: 16px;
}

.expired-text {
  font-size: 16px;
  color: #606266;
  margin-bottom: 20px;
}

.refresh-btn {
  padding: 10px 24px;
}

.expired-qrcode {
  opacity: 0.5;
  filter: grayscale(100%);
}

.expired-img {
  width: 200px;
  height: 200px;
  display: block;
  margin: 0 auto;
  border: 1px solid #f0f0f0;
}

.login-btn {
  width: 100%;
  margin-top: 8px;
  border-radius: 0;
  padding: 10px 16px;
  font-weight: 600;
  letter-spacing: 6px;
  font-size: 14px;
  transition: all 0.3s;
  box-shadow: 0 3px 8px rgba(64, 158, 255, 0.3);
  height: 42px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(64, 158, 255, 0.4);
  }
}

/* 注册链接样式优化 */
.register-link {
  text-align: center;
  font-size: 14px;
  padding-bottom: 15px;
  margin-top: 5px;
  border-bottom: 2px solid #d9d9d9;

  span {
    color: #606266;
    vertical-align: middle;
  }

  .el-link {
    font-size: 14px;
    vertical-align: middle;
  }
}

/* 协议复选框样式优化 */
.agreement {
  text-align: center;
  font-size: 12px;
  color: #909399;
  margin-top: 10px;
  line-height: 1.6;

  .agreement-content {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;

    .agree-checkbox {
      margin-right: 4px;

      // 防止点击复选框标签时触发链接
      :deep(.el-checkbox__label) {
        cursor: default;
        user-select: none;
      }
    }

    .agreement-links {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .el-link {
      font-size: 12px;
      vertical-align: baseline;

      &:hover {
        color: #409EFF;
      }
    }

    span {
      color: #909399;
      font-size: 12px;
    }
  }
}

/* 弹窗样式 */
.policy-dialog {
  .el-dialog {
    width: 50% !important;
    border-radius: 12px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    margin-top: 8vh !important;

    .el-dialog__header {
      padding: 16px 20px;
      margin: 0;
      border-bottom: 1px solid #e6e6e6;

      .dialog-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #2c3e50;
      }
    }

    .el-dialog__body {
      padding: 20px;
      flex: 1;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .el-dialog__footer {
      padding: 12px 20px;
      border-top: 1px solid #e6e6e6;
    }
  }
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  line-height: 1.6;
  color: #606266;
  padding-right: 10px;
  max-height: 70vh; /* 限制内容区域高度 */


  h2 {
    color: #2c3e50;
    margin-top: 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
    font-size: 1.4em;
  }

  h3 {
    color: #34495e;
    margin-top: 18px;
    margin-bottom: 10px;
    font-size: 1.2em;
  }

  p, ul {
    margin-bottom: 12px;
    font-size: 14px;
  }

  ul {
    padding-left: 18px;

    li {
      margin-bottom: 6px;
    }
  }

  strong {
    color: #2c3e50;
  }
}

.login-footer {
  color: #333;
  padding: 0;
  margin-top: auto;
  flex: 1;
  position: relative;

  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .footer-sections {
    display: grid;
    grid-template-columns: 2fr 2fr;
    gap: 40px;
    margin-bottom: 30px;
    padding: 10px;
    border-bottom: 1px solid rgb(1, 3, 11);
  }

  .footer-section:first-child {
    padding-right: 20px;
  }

  .footer-logo {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
  }

  .footer-logo .logo-img {
    height: 40px;
    width: auto;
    margin-right: 12px;
  }

  .logo-text {
    font-size: 1.5rem;
    font-weight: 700;
    color: #01030b;
    background: linear-gradient(45deg, #ff7e5f, #feb47b);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .footer-desc {
    color: rgb(1, 3, 11);
    line-height: 1.6;
    font-size: 0.95rem;
    margin: 0;
  }

  .section-title {
    color: #01030b;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 20px;
    position: relative;
  }

  .section-title::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 30px;
    height: 2px;
    background: linear-gradient(45deg, #ff7e5f, #feb47b);
    border-radius: 1px;
  }

  .footer-links {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .footer-links li {
    margin-bottom: 12px;
  }

  .footer-links a {
    color: rgb(1, 3, 11);
    text-decoration: none;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    display: inline-block;
  }

  .footer-links a:hover {
    color: #ff7e5f;
    transform: translateX(5px);
  }

  .contact-info {
    display: flex;
    flex-direction: row;
    gap: 20px;
    flex-wrap: wrap;
  }

  .contact-item {
    display: flex;
    align-items: center;
    gap: 10px;
    color: rgb(1, 3, 11);
    font-size: 0.9rem;
  }

  .contact-item .el-icon {
    color: #ff7e5f;
    font-size: 1.1rem;
  }

  /* 底部版权区域 */
  .footer-bottom {
    margin-top: -10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .copyright-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 200px;
  }

  .copyright-text {
    color: rgb(1, 3, 11);
    font-size: 0.9rem;
  }

  .legal-links {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .legal-link {
    color: rgb(1, 3, 11);
    text-decoration: none;
    font-size: 0.85rem;
    transition: color 0.3s ease;
  }

  .legal-link:hover {
    color: #ff7e5f;
    cursor: pointer !important;
  }

  .divider {
    color: rgb(1, 3, 11);
    font-size: 0.8rem;
  }

  .certification-info {
    display: flex;
    justify-content: center;
    gap: 20px;
  }

  .beian-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .beian-icon {
    height: 16px;
    width: auto;
  }

  .beian-link, .icp-link {
    color: rgb(1, 3, 11);
    text-decoration: none;
    font-size: 0.8rem;
    transition: color 0.3s ease;
  }

  .beian-link:hover, .icp-link:hover {
    color: #ff7e5f;
  }
}

// 笔记本屏幕适配 (762px - 1600px)
@media (max-width: 1600px) and (min-width: 762px) {
  .login-wrapper {
    width: 290px;
    height: 380px;
    transform: translateX(30vw);
  }

  .login-form-wrapper {
    padding: 15px; // 减少内边距
  }

  .login-header {
    margin-bottom: 15px;

    h3 {
      font-size: 18px; // 减小标题字体
      margin-bottom: 5px;
    }
  }

  .login-form {
    min-height: 150px;
    margin: 0 !important;
    flex-direction: column;
    justify-content: center;
  }

  // 调整表单元素大小
  :deep(.el-form-item) {
    margin-bottom: 10px;
  }

  .login-elegant-input {
    :deep(.el-input__wrapper) {
      height: 28px; // 减小输入框高度
      padding: 1px 8px;
    }

    :deep(.el-input__inner) {
      font-size: 11px; // 减小输入文字
      padding-right: 70px; // 调整按钮空间
    }

    .captcha-btn {
      font-size: 11px; // 验证码按钮文字缩小
    }
  }

  // 调整登录按钮
  .login-btn {
    height: 32px;
    padding: 8px 12px;
    font-size: 13px;
    letter-spacing: 4px;
    margin-top: 5px;
  }

  // 调整微信登录区域
  .wechat-login-container {
    height: 150px;
    padding: 15px 0;

    .wechat-qrcode {
      img {
        width: 150px;
        height: 150px;
      }

      .tip {
        font-size: 12px;
        margin-top: 10px;
      }
    }
  }

  // 调整注册链接
  .register-link {
    font-size: 11px;
    padding-bottom: 10px;
    margin-top: 3px;

    .el-link {
      font-size: 11px;
      vertical-align: middle;
    }
  }

  // 调整协议文字
  .agreement {
    font-size: 10px !important; // 进一步缩小字体
    margin-top: 8px;

    .agreement-content {
      display: flex;
      flex-wrap: nowrap !important; // 强制不换行
      align-items: center;
      justify-content: center;
      gap: 2px !important; // 减少间隙
      white-space: nowrap; // 防止文字换行
    }

    .agree-checkbox {
      margin-right: 2px !important;

      :deep(.el-checkbox__label) {
        font-size: 10px !important;
        white-space: nowrap;
      }
    }

    .agreement-links {
      display: flex;
      align-items: center;
      gap: 2px !important;
      white-space: nowrap;
    }

    .el-link {
      font-size: 10px !important;
      white-space: nowrap;
    }

    span {
      font-size: 10px !important;
      white-space: nowrap;
    }
  }

  // 微信登录时的协议样式
  .agreement:not(.agreement-content) {
    font-size: 10px !important;
    white-space: nowrap;

    .el-link {
      font-size: 10px !important;
    }
  }

  // 调整登录方式图标
  .login-method-tabs {
    gap: 10px;

    .method-button {
      width: 35px;
      height: 35px;
      padding: 6px !important;

      .method-icon {
        width: 24px;
        height: 24px;
      }
    }
  }

  // 调整错误提示
  .error-message,
  .success-message {
    font-size: 11px;
    height: 18px;

    .el-icon {
      font-size: 12px;
      margin-right: 3px;
    }
  }

  // 调整忘记密码链接
  .login-footer-link {
    .el-link {
      font-size: 12px;
    }
  }

  // 调整关闭按钮
  .close-btn {
    font-size: 16px;
    top: 8px;
    right: 8px;
  }
}
</style>